import { useContext } from 'react';
import useSWR from 'swr';

import { useAsyncWrapper } from '../../common/hooks-util';
import { useYupSync } from '../../common/yup-util';
import { DocumentItemSchema } from '../../components/document/schemas';
import { DocumentItem } from '../../components/document/types';
import ProjectContext, { useProject } from '../../contexts/project-context';
import { DocumentsService } from '../../generated/tellus';

export enum DocumentMoveConflictBehavior {
  Fail = 0,
  Replace = 1,
  Rename = 2,
}

export const useDocuments = (
  path?: string,
  includeChildren?: boolean,
  projectKey?: string
): {
  data: DocumentItem | undefined;
  loading: boolean;
  error: any;
  mutate: () => void;
  projectKey?: string;
} => {
  const { projectKey: contextProjectKey } = useProject();

  // if projectKey is an empty string, we should not fetch any documents
  const {
    data: fetchData,
    error: fetchError,
    isLoading,
    mutate,
  } = useSWR(
    projectKey === ''
      ? null
      : {
          _key: 'DocumentsService.getDocument',
          projectKey: projectKey ?? contextProjectKey,
          path,
          includeChildren,
        },
    DocumentsService.getDocument
  );

  const { data: yupData, error: yupError } = useYupSync(
    fetchData,
    DocumentItemSchema
  );

  if (projectKey === '') {
    return {
      data: undefined,
      loading: false,
      error: new Error('Project key is required'),
      mutate: () => {},
      projectKey: undefined,
    };
  }

  return {
    data: yupData,
    loading: isLoading,
    error: fetchError ?? yupError,
    mutate,
    projectKey: projectKey ?? contextProjectKey,
  };
};

export const useDeleteItem = () => {
  const { projectKey } = useProject();

  return useAsyncWrapper(async (path: string) => {
    await DocumentsService.deleteDocument({
      projectKey,
      path,
    });
  });
};

export const useDocumentsOperations = () => {
  const projectKey = useContext(ProjectContext).project?.projectKey;

  if (!projectKey) {
    return {
      copyItems: null,
      moveItems: null,
    };
  }

  const copyItems = async (
    selectedDocuments: string[],
    targetProjectKey: string,
    targetPath: string
  ) => {
    return DocumentsService.moveDocuments({
      projectKey: projectKey,
      requestBody: {
        sourceItems: selectedDocuments.map((doc) => doc.toString()),
        targetProjectKey: targetProjectKey,
        targetPath,
        keepSourceDocuments: true,
        conflictBehavior: DocumentMoveConflictBehavior.Rename,
      },
    });
  };

  const moveItems = async (
    selectedDocuments: string[],
    targetProjectKey: string,
    targetPath: string
  ) => {
    return DocumentsService.moveDocuments({
      projectKey: projectKey,
      requestBody: {
        sourceItems: selectedDocuments.map((doc) => doc.toString()),
        targetProjectKey: targetProjectKey,
        targetPath,
        keepSourceDocuments: false,
        conflictBehavior: DocumentMoveConflictBehavior.Rename,
      },
    });
  };

  return {
    copyItems,
    moveItems,
  };
};
